import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import ContactFormModal from '../components/ContactFormModal';

const BookMarketing = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const services = [
    {
      title: 'Social Media Marketing',
      description: 'Strategic social media campaigns across Facebook, Instagram, Twitter, and TikTok to engage readers.',
      features: ['Content creation', 'Daily posting', 'Community engagement', 'Influencer outreach']
    },
    {
      title: 'Amazon Marketing',
      description: 'Optimize your Amazon presence with keyword optimization, A+ content, and Amazon Ads campaigns.',
      features: ['Amazon SEO', 'A+ Content', 'Amazon Ads', 'Review generation']
    },
    {
      title: 'Book Launch Campaign',
      description: 'Complete launch strategy including pre-launch buzz, launch day coordination, and post-launch momentum.',
      features: ['Launch strategy', 'Email campaigns', 'Media outreach', 'Launch events']
    },
    {
      title: 'PR & Media Coverage',
      description: 'Secure media coverage, podcast interviews, and press features to boost your book\'s visibility.',
      features: ['Press releases', 'Media pitching', 'Podcast bookings', 'Interview prep']
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Strategy Development & Planning',
      description: 'We analyze your book, target audience, and goals to create a customized marketing strategy with clear objectives and measurable KPIs.',
      icon: 'https://cdn-icons-png.flaticon.com/512/3524/3524659.png'
    },
    {
      step: '02',
      title: 'Campaign Launch & Execution',
      description: 'Execute multi-channel marketing campaigns including social media, Amazon optimization, email marketing, and PR outreach to maximize reach.',
      icon: 'https://cdn-icons-png.flaticon.com/512/2521/2521826.png'
    },
    {
      step: '03',
      title: 'Optimization & Reporting',
      description: 'Monitor campaign performance, optimize based on data, and provide detailed reports showing sales impact and ROI from marketing efforts.',
      icon: 'https://cdn-icons-png.flaticon.com/512/3524/3524388.png'
    }
  ];

  const whyChooseUs = [
    {
      icon: 'https://cdn-icons-png.flaticon.com/512/3330/3330315.png',
      title: 'Proven Results',
      description: 'Our marketing campaigns have helped books reach bestseller status on Amazon and major lists.'
    },
    {
      icon: 'https://cdn-icons-png.flaticon.com/512/2490/2490402.png',
      title: 'Multi-Channel Approach',
      description: 'We market across all relevant channels: social media, Amazon, email, PR, and advertising.'
    },
    {
      icon: 'https://cdn-icons-png.flaticon.com/512/2838/2838779.png',
      title: 'Data-Driven Strategy',
      description: 'Every campaign is backed by analytics and optimized based on real performance data.'
    },
    {
      icon: 'https://cdn-icons-png.flaticon.com/512/3524/3524715.png',
      title: 'Transparent Reporting',
      description: 'Detailed monthly reports showing exactly where your budget goes and the results achieved.'
    }
  ];

  const faqs = [
    {
      question: 'How much does book marketing cost?',
      answer: 'Marketing costs vary widely based on campaign scope, duration, and channels. Basic campaigns start around $2,000/month for social media and Amazon optimization. Comprehensive launch campaigns with PR and advertising range from $5,000-$15,000+ for a 3-month campaign. We create customized packages based on your budget and goals.'
    },
    {
      question: 'When should I start marketing my book?',
      answer: 'Ideally, start 3-6 months before launch to build pre-launch buzz. However, we can create effective campaigns at any stage - pre-launch, during launch, or post-launch to revitalize sales. Earlier is better for building momentum, but it\'s never too late to start marketing.'
    },
    {
      question: 'Can you guarantee my book will be a bestseller?',
      answer: 'No one can guarantee bestseller status - it depends on many factors including book quality, genre, timing, and market conditions. However, we use proven strategies that have helped numerous books reach bestseller lists on Amazon and major publications. We focus on maximizing your visibility and sales potential.'
    },
    {
      question: 'What results can I expect from marketing?',
      answer: 'Results vary by genre, competition, and campaign budget. Typical well-executed campaigns see 50-300% increase in sales during active marketing periods. We provide realistic projections during strategy planning and track all metrics to show clear ROI on your marketing investment.'
    },
    {
      question: 'Do I need a marketing budget for ads?',
      answer: 'While organic marketing (social media, PR) can be effective, paid advertising significantly amplifies reach. We recommend budgeting $500-$2,000/month for ads (Amazon Ads, Facebook Ads) separate from our service fees. We can work with various budgets and optimize for maximum ROI.'
    },
    {
      question: 'How long should a marketing campaign run?',
      answer: 'We recommend minimum 3-month campaigns for meaningful results. Book launch campaigns typically run 3-6 months (1-2 months pre-launch, launch month, 2-3 months post-launch). Ongoing marketing for sustained sales works best with 6-12 month contracts with monthly optimization.'
    },
    {
      question: 'Will you handle my social media accounts?',
      answer: 'Yes, we can fully manage your author social media accounts or work alongside your existing presence. This includes content creation, daily posting, community engagement, and follower growth strategies across relevant platforms for your genre and audience.'
    },
    {
      question: 'Do you work with all book genres?',
      answer: 'Yes, we have experience marketing all genres including fiction (all sub-genres), non-fiction, business books, memoirs, and specialized topics. We tailor strategies to your specific genre\'s audience and market dynamics.'
    }
  ];

  const testimonials = [
    {
      name: 'Amanda Clarke',
      title: 'Romance Author',
      text: 'The marketing campaign helped my book reach #1 in its category on Amazon! The team\'s social media strategy and Amazon ads brought incredible visibility and sales.',
      rating: 5
    },
    {
      name: 'Robert Hayes',
      title: 'Business Author',
      text: 'Professional, strategic, and results-driven. The PR outreach landed me podcast interviews and media features I never could have secured alone. Book sales tripled during the campaign.',
      rating: 5
    },
    {
      name: 'Lisa Wong',
      title: 'Thriller Author',
      text: 'Best investment I made for my book. The launch campaign created real buzz, and the ongoing marketing keeps my sales steady. They truly understand book marketing.',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-amber-50 to-white pt-24 py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 border-2 border-amber-700 rounded-full mb-6">
                <span className="text-amber-700 text-sm font-semibold tracking-wider">BOOK MARKETING SERVICES</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Market Your Book to <span className="text-amber-700">Bestseller Status</span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Strategic book marketing campaigns that drive sales and build your author brand. From social media to Amazon optimization to PR outreach - we create comprehensive campaigns that get results.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Button 
                  onClick={() => setIsModalOpen(true)}
                  className="bg-amber-700 hover:bg-amber-800 text-white font-bold py-6 px-10 text-lg rounded-full shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
                >
                  Get Marketing Plan
                </Button>
                <Button 
                  onClick={() => scrollToSection('process')}
                  className="bg-white hover:bg-gray-50 text-amber-700 border-2 border-amber-700 font-bold py-6 px-10 text-lg rounded-full shadow-lg hover:shadow-xl transition-all"
                >
                  See How It Works
                </Button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                <div>
                  <div className="text-4xl font-bold text-amber-700 mb-2">300+</div>
                  <div className="text-gray-600 text-sm">Campaigns Run</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-amber-700 mb-2">50+</div>
                  <div className="text-gray-600 text-sm">Bestsellers</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-amber-700 mb-2">250%</div>
                  <div className="text-gray-600 text-sm">Avg Sales Increase</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-amber-700 mb-2">5M+</div>
                  <div className="text-gray-600 text-sm">Readers Reached</div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative z-10">
                <img 
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop" 
                  alt="Book marketing"
                  className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6 max-w-xs">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center p-3">
                      <img 
                        src="https://cdn-icons-png.flaticon.com/512/3330/3330315.png" 
                        alt="Campaigns"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">50+</div>
                      <div className="text-sm text-gray-600">Bestsellers Created</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-amber-200 rounded-full opacity-20 blur-2xl"></div>
              <div className="absolute -bottom-6 right-12 w-40 h-40 bg-amber-300 rounded-full opacity-20 blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Marketing Channels Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 relative">
              <img 
                src="https://images.unsplash.com/photo-1432888622747-4eb9a8f2c0e9?w=800&h=600&fit=crop" 
                alt="Multi-channel marketing"
                className="rounded-2xl shadow-2xl w-full h-[450px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-amber-700/20 to-transparent rounded-2xl"></div>
            </div>

            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-6">
                <span className="text-amber-700 text-sm font-semibold tracking-wider">MULTI-CHANNEL MARKETING</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Reach Readers <span className="text-amber-700">Everywhere</span>
              </h2>
              
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                We market your book across all channels where readers discover books - social media, Amazon, email, PR, and targeted advertising.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center p-3">
                    <img 
                      src="https://cdn-icons-png.flaticon.com/512/3330/3330315.png" 
                      alt="Social Media"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Social Media Marketing</h3>
                    <p className="text-gray-600">Build author brand and engage readers on Facebook, Instagram, Twitter, and TikTok.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center p-3">
                    <img 
                      src="https://cdn-icons-png.flaticon.com/512/2521/2521826.png" 
                      alt="Amazon Marketing"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Amazon Optimization</h3>
                    <p className="text-gray-600">Maximize visibility with SEO, A+ content, and strategic Amazon Ads campaigns.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center p-3">
                    <img 
                      src="https://cdn-icons-png.flaticon.com/512/1087/1087927.png" 
                      alt="PR & Media"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">PR & Media Coverage</h3>
                    <p className="text-gray-600">Secure podcast interviews, press features, and media coverage to boost credibility.</p>
                  </div>
                </div>
              </div>

              <Button 
                onClick={() => setIsModalOpen(true)}
                className="bg-amber-700 hover:bg-amber-800 text-white font-bold py-6 px-10 text-lg rounded-full shadow-lg hover:shadow-xl transition-all"
              >
                Start Your Campaign
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Creative CTA */}
      <section className="py-20 bg-gradient-to-br from-amber-700 via-amber-600 to-amber-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Don't Let Your Book Go Unnoticed
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Even great books need marketing to find their readers. Strategic marketing turns published books into bestsellers.
            </p>
            <Button 
              onClick={() => setIsModalOpen(true)}
              className="bg-white hover:bg-gray-100 text-amber-700 font-bold py-6 px-10 text-lg rounded-full shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
            >
              Get Free Marketing Consultation
            </Button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Complete <span className="text-amber-700">Marketing Services</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive marketing solutions to maximize your book's visibility and sales.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {services.map((service, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <ul className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-amber-700 flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-6">
              <span className="text-amber-700 text-sm font-semibold tracking-wider">OUR PROCESS</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              How It <span className="text-amber-700">Works</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Data-driven marketing campaigns designed to maximize your book's reach and sales.
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 relative">
              <div className="hidden md:block absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-amber-200 via-amber-400 to-amber-200" style={{ top: '80px' }}></div>

              {process.map((item, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 h-full border-t-4 border-amber-500 relative z-10">
                    <div className="absolute -top-6 left-8">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center shadow-lg transform hover:scale-110 transition-transform">
                        <span className="text-2xl font-bold text-white">{item.step}</span>
                      </div>
                    </div>

                    <div className="mt-8 mb-6 flex justify-center">
                      <div className="w-24 h-24 bg-gradient-to-br from-amber-50 to-amber-100 rounded-full flex items-center justify-center p-5">
                        <img 
                          src={item.icon} 
                          alt={item.title}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed text-center">{item.description}</p>

                    {index < process.length - 1 && (
                      <div className="hidden md:block absolute -right-4 top-20 text-amber-500">
                        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>

                  {index < process.length - 1 && (
                    <div className="md:hidden flex justify-center py-4">
                      <svg className="w-8 h-8 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v10.586l3.293-3.293a1 1 0 111.414 1.414l-5 5a1 1 0 01-1.414 0l-5-5a1 1 0 111.414-1.414L9 14.586V4a1 1 0 011-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-16">
            <p className="text-gray-600 mb-6 text-lg">Ready to market your book?</p>
            <Button 
              onClick={() => setIsModalOpen(true)}
              className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-bold py-6 px-12 text-lg rounded-full shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
            >
              Launch Your Campaign
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-6">
                <span className="text-amber-700 text-sm font-semibold tracking-wider">WHY CHOOSE US</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Why Choose <span className="text-amber-700">Rushmore Publishing</span>
              </h2>
              
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Results-driven marketing backed by data, creativity, and years of experience in book promotion.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                {whyChooseUs.map((item, index) => {
                  return (
                    <div key={index} className="flex gap-4">
                      <div className="flex-shrink-0 w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center p-3">
                        <img 
                          src={item.icon} 
                          alt={item.title}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                        <p className="text-gray-600 text-sm">{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&h=600&fit=crop" 
                alt="Marketing results"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-amber-700 text-white rounded-2xl shadow-xl p-6 max-w-xs">
                <div className="text-4xl font-bold mb-2">250%</div>
                <div className="text-sm">Average Sales Increase</div>
                <div className="mt-3 text-xs opacity-90">During active campaigns</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1920&h=800&fit=crop" 
            alt="Start marketing"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-amber-900/95 to-amber-700/95"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center text-white">
          <div className="max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/20 backdrop-blur-sm rounded-full mb-8">
              <span className="text-white text-sm font-semibold tracking-wider">FREE MARKETING CONSULTATION</span>
            </div>
            
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              Ready to Make Your Book a Bestseller?
            </h2>
            <p className="text-xl mb-12 opacity-90 leading-relaxed">
              Let's create a custom marketing strategy for your book. Get a free consultation to discuss your goals and budget.
            </p>
            
            <Button 
              onClick={() => setIsModalOpen(true)}
              className="bg-white hover:bg-gray-100 text-amber-700 font-bold py-6 px-12 text-xl rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all"
            >
              Get Your Free Marketing Plan
            </Button>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto pt-12 border-t border-white/20 mt-12">
              <div>
                <div className="text-3xl font-bold mb-2">300+</div>
                <div className="text-sm opacity-80">Campaigns Run</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">50+</div>
                <div className="text-sm opacity-80">Bestsellers Created</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">250%</div>
                <div className="text-sm opacity-80">Avg Sales Increase</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">24/7</div>
                <div className="text-sm opacity-80">Support Available</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What Our <span className="text-amber-700">Authors Say</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-amber-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <div className="font-bold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">{testimonial.title}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked <span className="text-amber-700">Questions</span>
            </h2>
          </div>

          <div className="max-w-3xl mx-auto">
            {faqs.map((faq, index) => (
              <div key={index} className="mb-4">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full bg-gray-50 hover:bg-gray-100 p-6 rounded-xl text-left flex items-center justify-between transition-all"
                >
                  <span className="font-bold text-gray-900 text-lg pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-6 h-6 text-amber-700 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-6 h-6 text-amber-700 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="bg-white p-6 rounded-b-xl border-l-4 border-amber-700">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <ContactFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default BookMarketing;
